import { useMemo } from 'react';
import { TableIcon, Users, ShoppingBag, Clock, RefreshCw, AlertCircle, Loader2 } from 'lucide-react';

function elapsed(createdAt) {
  const diff = Math.floor((Date.now() - new Date(createdAt).getTime()) / 1000);
  const m = Math.floor(diff / 60);
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}h ${m % 60}m`;
  return `${m}m`;
}

function fmtPrice(centavos) {
  return `₱${(centavos / 100).toFixed(2)}`;
}

export default function FloorView({
  tables,
  activeOrdersMap = {},
  isLoading,
  error,
  onRefresh,
  onSelectAvailable,  // (table) → pax step
  onViewOrder,        // (table, order) → OrderDetailPanel
}) {
  const sections = useMemo(() => {
    const map = {};
    for (const t of tables) {
      const key = t.section || 'Main';
      if (!map[key]) map[key] = [];
      map[key].push(t);
    }
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [tables]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 text-red-600">
        <AlertCircle className="h-6 w-6" />
        <p className="text-sm">{error}</p>
        <button onClick={onRefresh} className="text-sm text-blue-600 underline">Retry</button>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-4 space-y-5">
      {sections.map(([section, sectionTables]) => (
        <div key={section}>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{section}</p>
          <div className="grid grid-cols-3 gap-3">
            {sectionTables.map((t) => {
              const order = activeOrdersMap[t._id];
              const isOccupied = t.status === 'occupied';
              const isAvailable = t.status === 'available';
              const activeItems = order?.items?.filter(i => !i.voidedAt) || [];
              const itemCount = activeItems.reduce((s, i) => s + i.quantity, 0);

              return (
                <button
                  key={t._id}
                  type="button"
                  onClick={() => {
                    if (isOccupied && order) onViewOrder(t, order);
                    else if (isAvailable) onSelectAvailable(t);
                  }}
                  disabled={t.status === 'reserved'}
                  className={`relative rounded-xl border-2 p-3 text-left transition-all ${
                    isAvailable
                      ? 'border-green-400 bg-green-50 hover:bg-green-100 cursor-pointer'
                      : isOccupied
                      ? 'border-red-400 bg-red-50 hover:bg-red-100 cursor-pointer'
                      : 'border-yellow-400 bg-yellow-50 cursor-not-allowed opacity-70'
                  }`}
                >
                  {/* Table name + status */}
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="font-bold text-gray-900 text-sm flex items-center gap-1">
                      <TableIcon className="h-3.5 w-3.5" /> {t.name}
                    </span>
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                      isAvailable ? 'bg-green-200 text-green-800'
                      : isOccupied ? 'bg-red-200 text-red-800'
                      : 'bg-yellow-200 text-yellow-800'
                    }`}>
                      {t.status}
                    </span>
                  </div>

                  {/* Order data for occupied tables */}
                  {isOccupied && order ? (
                    <div className="space-y-1">
                      <div className="flex items-center gap-1 text-xs text-gray-600">
                        <Users className="h-3 w-3" />
                        <span>{order.pax || 1} guest{(order.pax || 1) !== 1 ? 's' : ''}</span>
                        <span className="mx-1">·</span>
                        <Clock className="h-3 w-3" />
                        <span>{elapsed(order.createdAt)}</span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-600">
                        <ShoppingBag className="h-3 w-3" />
                        <span>{itemCount} item{itemCount !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="text-xs font-bold text-gray-900">
                        {fmtPrice(order.totalAmount || 0)}
                      </div>
                      <div className="text-[10px] text-gray-400 truncate">
                        {order.waiterName}
                      </div>
                    </div>
                  ) : isAvailable ? (
                    <p className="text-xs text-green-600 mt-1">Tap to seat guests</p>
                  ) : (
                    <p className="text-xs text-yellow-700 mt-1">Reserved</p>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {tables.length === 0 && (
        <div className="flex flex-col items-center justify-center h-40 text-gray-400 gap-2">
          <TableIcon className="h-8 w-8" />
          <p className="text-sm">No tables found</p>
        </div>
      )}
    </div>
  );
}
